import React from "react";

import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import i18next from "i18next";
import styled from "styled-components";

import {
  useGetLockedDeliveryDates,
  useGetMpDistributorDepots,
} from "api/reactQuery/queryComponents";
import {
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
  BSCoreWebRequestsResponsesDistributorsDistributorResponse,
} from "api/reactQuery/querySchemas";

import { OrderInfoLabel } from "./styles";
import WarehouseCard from "../components/WarehouseCard";

type Props = {
  resetCalculatedData: () => void;
  selectedDistributor: BSCoreWebRequestsResponsesDistributorsDistributorResponse;
  selectedDepots: BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[];
  setSelectedDepots: React.Dispatch<
    React.SetStateAction<
      BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[]
    >
  >;
  setDeliveryDate: React.Dispatch<React.SetStateAction<Dayjs>>;
  deliveryDate: Dayjs;
  saveAndSendLoading: boolean;
};

const SelectWarehouseStep: React.FC<Props> = ({
  selectedDistributor,
  selectedDepots,
  setSelectedDepots,
  setDeliveryDate,
  deliveryDate,
  saveAndSendLoading,
}) => {
  const { data: distributorDepots, isLoading: depotsLoading } =
    useGetMpDistributorDepots(
      {
        queryParams: { distributorId: selectedDistributor?.id },
      },
      { enabled: !!selectedDistributor?.id }
    );

  const { data: lockedDeliveryDates } = useGetLockedDeliveryDates({});

  const lockedDeliveryDatesAsString = lockedDeliveryDates?.map((date) => {
    return date.toString();
  });

  const disableDates = (current: Dayjs) => {
    const isLockedDate = lockedDeliveryDatesAsString?.includes(
      current.format("YYYY-MM-DD")
    );
    if (isLockedDate) return true;

    // Disable all Sundays
    return current.day() === 0;
  };

  return (
    <Container>
      <div>
        <SectionTitle>{i18next.t("choose_warehouse")}</SectionTitle>
        {depotsLoading ? (
          <p>Loading...</p>
        ) : (
          distributorDepots.map((depot) => {
            return (
              <WarehouseCard
                key={depot.id}
                depot={depot}
                selectedDepots={selectedDepots}
                setSelectedDepots={setSelectedDepots}
              />
            );
          })
        )}
      </div>

      <div>
        <SectionTitle>{i18next.t("choose_delivery_date")}</SectionTitle>
        <DatePickerWrapper>
          <OrderInfoLabel>{i18next.t("delivery_date")}</OrderInfoLabel>
          <DatePicker
            showNow={false}
            allowClear={false}
            value={deliveryDate}
            disabled={saveAndSendLoading}
            disabledDate={disableDates}
            onChange={(date) => {
              setDeliveryDate(date);
            }}
            minDate={dayjs().add(1, "day")}
            format="DD/MM/YYYY"
          />
        </DatePickerWrapper>
      </div>
    </Container>
  );
};

export default SelectWarehouseStep;

const Container = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;

  & > div {
    width: 50%;
    background: #f9f9f9;
    padding: 16px;
    border-radius: 24px;
    display: flex;
    gap: 12px;
    flex-direction: column;
  }
`;

const SectionTitle = styled.h1`
  font-weight: 400;
  font-size: 20px;
  color: #444;
  padding-left: 10px;
  margin-bottom: 36px;
  font-size: 28px;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;
